import { defineComponent as _defineComponent } from 'vue'
import {computed, onBeforeUnmount, onMounted, Ref, ref, watch} from "vue";
import {onBeforeRouteLeave, useRouter} from "vue-router/composables";
import {usePickStore} from "@/stores/tasks/pick-tasks";
import {useOrdersStore} from "@/stores/orders";
import ToteContents from "@/components/pages/WavePicking/ToteContents.vue";
import {bus} from "@/bootstrap/bus";
import {SmartOrderMapped} from "@/types/ApiSmartOrders";
import ordersApiGateway from "@/api/ordersApiGateway";
import {decodeToteScan} from "@/store/wavePicking/helpers";
import store from "@/bootstrap/store";
import i18n from "@/plugins/i18n";
import NotificationTypes from "@/enums/NotificationTypes";


export default /*#__PURE__*/_defineComponent({
  __name: 'WavePickComplete',
  setup(__props) {

const router = useRouter();
const pickStore = usePickStore();
const orderStore = useOrdersStore();

const isLoading = ref(false);
const packageIdentifier: Ref<null | string> = ref(null);
const toteIsConfirmed = ref(false);
const enableRouteLeave = ref<boolean>(false);

// disable menu as we do not want to navigate away
onBeforeRouteLeave((to, _from, next) => {
  const exitRoutes = [
    'wave_pick_start',
  ];
  if (to.name && exitRoutes.includes(to.name) && enableRouteLeave.value) {
    next();
  } else {
    next(false)
  }
});

const handleItemScan = (barcode: string) => {
  if (!toteIsConfirmed.value && isValidToteScan(barcode)) {
    toteIsConfirmed.value = true;
    return;
  }

  if (
      toteIsConfirmed.value
      && !isValidPackageIdentifier(packageIdentifier.value)
      && isValidPackageIdentifier(barcode)
  ) {
    packageIdentifier.value = barcode;
    return;
  }

  if (allowSubmit.value && isValidToteScan(barcode)) {
    completeTotePick();
  }
}

const isValidToteScan = (barcode: string): boolean => {
  const label = decodeToteScan(barcode)
  return label && label.identifier === currentTote.value?.name
}

const currentTote = computed(() => {
  return pickStore.scannedTotes.length > 0 ? pickStore.scannedTotes[0] : null;
})

const order = computed(() => {
  if (currentTote.value?.orderHeaderId) {
    toteIsConfirmed.value = false;
    packageIdentifier.value = null;
    return orderStore.getOrderById(currentTote.value.orderHeaderId);
  }
  return null
})

const allowSubmit = computed(() => {
  return toteIsConfirmed.value && isValidPackageIdentifier(packageIdentifier.value)
})

const ordersToProcess = pickStore.scannedTotes.length
const ordersPicked = computed(() => ordersToProcess - pickStore.scannedTotes.length + 1)

const isValidPackageIdentifier = (packageIdentifier: string | null): boolean => {
  return !!packageIdentifier && packageIdentifier.length === 12
};

// It's possible we don't need to make this call as we're not packing in the wave picking flow
// maybe check with Data Science what is needed for Perfect Workforce?
const markOrderInProgress = async (order: SmartOrderMapped) => {
  // auditLog.logStartPacking(order);
  await ordersApiGateway.setAsInProgress(order.id)
}

const completeTotePick = async() => {
  if (
      currentTote.value
      && currentTote.value.orderHeaderId
      && packageIdentifier.value
  ) {
    try {
      isLoading.value = true;
      await ordersApiGateway.setAsPickComplete(currentTote.value.orderHeaderId, packageIdentifier.value)
      pickStore.actions.removeTote(currentTote.value);
      enableRouteLeave.value = true;
    } catch (e) {
      // Error is handled in API helper but rethrown, so we don't need to do anything
      if (e.isAxiosError && e.response.status === 422) {
        const isPiLabelError = e.response.data.validationErrors.some((error) => {
          return error.param === "package_identifier";
        })
        if (isPiLabelError) {
          packageIdentifier.value = null;
        }
      }
    } finally {
      isLoading.value = false;
    }
  }
}

watch(currentTote, (newValue) => {
  if (newValue === null) {
    store.dispatch('core/addNotification', {
      message: i18n.t('wave_picking.complete'),
      type: NotificationTypes.NOTIFICATION_TYPE_SUCCESS,
      timeout: 3500,
    });
    router.push({name: "wave_pick_start"});
  }
});

watch(order, (newValue) => {
  if (newValue) {
    markOrderInProgress(newValue);
  }
}, { immediate: true });

onBeforeUnmount(() => {
  bus.$off("barcodeScanned", handleItemScan);
});

onMounted(() => {
  bus.$on("barcodeScanned", handleItemScan);
});

return { __sfc: true,router, pickStore, orderStore, isLoading, packageIdentifier, toteIsConfirmed, enableRouteLeave, handleItemScan, isValidToteScan, currentTote, order, allowSubmit, ordersToProcess, ordersPicked, isValidPackageIdentifier, markOrderInProgress, completeTotePick, ToteContents }
}

})